import { Text, View, Document, Page, Font, Link, Image } from '@react-pdf/renderer';
import { spacing, styles } from './styles';
import { format } from 'date-fns';
import arialRegular from '../../assets/fonts/ARIAL.TTF';
import arialBold from '../../assets/fonts/ARIALBD.TTF';
import arialItalic from '../../assets/fonts/ARIALI.TTF';
import { UseResumeDataQuery, MilitaryExperience, Experience } from 'lib/hooks/useResumeDataQuery';
import { formatPhoneNumber } from 'lib/utils';
import { Fragment } from 'react';
import { BRANCH_OPTIONS, RANKS_BY_BRANCH } from 'constants/militaryOptions';
import { EDUCATION_LEVEL_BY_ID, EDUCATION_CREDENTIAL_LABEL_IDS } from 'constants/educationLevelOptions';
import { lcwTypeLabels } from 'components/forms/experienceWallet/ExperienceWalletDrawer';

Font.register({
  family: 'Arial',
  fonts: [{ src: arialRegular }, { src: arialBold, fontWeight: 600 }, { src: arialItalic, fontStyle: 'italic' }],
});

// Disable hyphenation - words will wrap to next line instead of being broken with hyphens
Font.registerHyphenationCallback((word) => [word]);

/** Handles both ISO date strings ("2023-01-01") and "MM/YYYY" format from the form */
function parseAnyDate(str: string | null | undefined): Date | null {
  if (!str) return null;
  // MM/YYYY format
  if (/^\d{2}\/\d{4}$/.test(str)) {
    const [mm, yyyy] = str.split('/').map(Number);
    return new Date(yyyy, mm - 1, 1);
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const [yyyy, mm] = str.split('-').map(Number);
    return new Date(yyyy, mm - 1, 1);
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(str: string | null | undefined): string {
  const d = parseAnyDate(str);
  return d ? format(d, 'MMM yyyy') : '';
}

function parseDateMs(str: string | null | undefined): number {
  const d = parseAnyDate(str);
  return d ? d.getTime() : 0;
}

function compareExperienceDates(a: Experience | MilitaryExperience, b: Experience | MilitaryExperience): number {
  const aIsCurrent = a.is_current === true || a.end_date === null || a.end_date === undefined;
  const bIsCurrent = b.is_current === true || b.end_date === null || b.end_date === undefined;
  if (aIsCurrent !== bIsCurrent) return aIsCurrent ? -1 : 1;
  const endDateDiff = parseDateMs(b.end_date) - parseDateMs(a.end_date);
  if (endDateDiff !== 0) return endDateDiff;
  return parseDateMs(b.start_date) - parseDateMs(a.start_date);
}

function formatDateRangeOrSingle(
  start: string | null | undefined,
  end: string | null | undefined,
  presentLabel: string
): string {
  if (!start) return '';
  const startStr = formatDate(start);
  if (end && end !== start) return `${startStr} – ${formatDate(end)}`;
  if (!end) return `${startStr} – ${presentLabel}`;
  return startStr;
}

const ALL_RANKS = Object.values(RANKS_BY_BRANCH as Record<string, { value: string; label: string }[]>).flat();

const EDUCATION_TYPE_LABEL_BY_KEY = new Map<string, string>([
  ['assessment', 'Assessment'],
  ['badge', 'Badge'],
  // Backend CredentialTypeEnum.openbadge covers both LER "OpenBadgeCredential" and
  // "AchievementCredential" types — both are badges to the user.
  ['openbadge', 'Badge'],
  // Defensive: in case the backend ever sends this raw/unnormalized instead of openbadge.
  ['achievement', 'Achievement'],
  ['AchievementCredential', 'Achievement'],
  ['course', 'Course'],
  ['militaryCourse', 'Military Course'],
  ['certificate', 'Certificate'],
  ['certification', 'Certification'],
  ['license', 'License'],
  ['apperenticeshipRTI', 'Apprenticeship Related Technical Instruction (RTI)'],
  ['degree', 'Education Degree'],
]);

function getMilitaryLabels(mil: MilitaryExperience): { branchLabel: string; rankLabel: string } {
  const branchLabel = BRANCH_OPTIONS.find((b) => b.value === mil.branch)?.backendName || mil.branch || '';
  const ranksList = mil.branch
    ? (RANKS_BY_BRANCH as Record<string, { value: string; label: string }[]>)[mil.branch]
    : null;
  const rankLabel = ranksList
    ? ranksList.find((r) => r.value === mil.rank)?.label ||
      ALL_RANKS.find((r) => r.value === mil.rank)?.label ||
      mil.rank ||
      ''
    : ALL_RANKS.find((r) => r.value === mil.rank)?.label || mil.rank || '';
  return { branchLabel, rankLabel };
}

function PDFDocumentTemplate({
  resumeData,
  t,
}: {
  resumeData: UseResumeDataQuery['data'] | null;
  t: (id: string, values?: Record<string, unknown>) => string;
}) {
  if (!resumeData) return null;
  return (
    <Document>
      <Page
        size="A4"
        style={{
          ...styles.flexCol,
          fontSize: '12pt',
          fontFamily: 'Arial',
          padding: `${spacing[4]} ${spacing[6]}`,
        }}
      >
        <View
          style={{
            ...styles.flexCol,
          }}
        >
          {/* HEADER SECTION */}
          <View style={{ marginBottom: spacing[2] }}>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <View style={{ ...styles.flexCol }}>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: spacing[0.5],
                  }}
                >
                  {resumeData.header.showMilitaryStatus && (
                    <Image
                      src="data:image/png;base64,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"
                      style={{ width: 20, marginRight: 6, alignSelf: 'stretch', objectFit: 'contain' }}
                    />
                  )}
                  <Text
                    style={{
                      fontSize: '20pt',
                      textTransform: 'uppercase',
                    }}
                  >
                    {resumeData.header.name}
                  </Text>
                </View>
                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {[
                    resumeData.header.location,
                    resumeData.header.phone_number &&
                      resumeData.header.phone_number.length === 10 &&
                      `${formatPhoneNumber(resumeData.header.phone_number)}`,
                    resumeData.header.email && (
                      <Link
                        style={{
                          color: 'black',
                          textDecoration: 'none',
                        }}
                        src={`mailto:${resumeData.header.email}`}
                      >
                        {resumeData.header.email}
                      </Link>
                    ),
                    resumeData.header.url && (
                      <Link
                        style={{
                          color: 'black',
                          textDecoration: 'none',
                        }}
                        src={`https://${resumeData.header.url}`}
                      >
                        {resumeData.header.url}
                      </Link>
                    ),
                  ]
                    .filter(Boolean) // Filter out falsy values
                    .map((item, index) => (
                      <View key={index} style={{ flexDirection: 'row', alignItems: 'center' }}>
                        {index > 0 && <Text style={{ marginHorizontal: 4 }}>|</Text>}
                        <Text>{item}</Text>
                      </View>
                    ))}
                </View>
              </View>
            </View>
          </View>

          {resumeData.sections.map((section) => {
            switch (section.type) {
              case 'summary':
                if (section.items.length <= 0 || !section.items[0]) return null;
                return section.items.map((item, index) => (
                  <View
                    key={index}
                    style={{
                      ...styles.flexCol,
                      marginBottom: spacing[2],
                    }}
                  >
                    <Text
                      style={{
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        borderBottom: '1px solid black',
                        marginBottom: spacing[1],
                      }}
                    >
                      {t('createResume.pdfDocumentTemplate.summary')}
                    </Text>

                    <Text>{item}</Text>
                  </View>
                ));
              case 'skills': {
                if (section.items.length <= 0) return null;

                const limitedSkills = section.items.slice(0, 20);
                const midPoint = Math.ceil(limitedSkills.length / 2);
                const leftColumnSkills = limitedSkills.slice(0, midPoint);
                const rightColumnSkills = limitedSkills.slice(midPoint);

                return (
                  <View
                    key={section.type}
                    style={{
                      ...styles.flexCol,
                      marginBottom: spacing[2],
                    }}
                  >
                    <Text
                      style={{
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        borderBottom: '1px solid black',
                        // marginBottom: spacing[1],
                      }}
                    >
                      {t('createResume.pdfDocumentTemplate.skills')}
                    </Text>

                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <View style={{ flex: 1, paddingRight: spacing[1] }}>
                        {leftColumnSkills.map((skill, index) => (
                          <View
                            key={index}
                            style={{
                              flexDirection: 'row',
                              marginBottom: spacing[0.5],
                              width: '100%',
                            }}
                          >
                            <Text style={{ width: '8pt', flexShrink: 0 }}>•</Text>
                            <Text style={{ flex: 1, paddingLeft: '3pt' }}>{skill.name}</Text>
                          </View>
                        ))}
                      </View>

                      <View style={{ flex: 1, paddingLeft: spacing[1] }}>
                        {rightColumnSkills.map((skill, index) => (
                          <View
                            key={index}
                            style={{
                              flexDirection: 'row',
                              marginBottom: spacing[0.5],
                              width: '100%',
                            }}
                          >
                            <Text style={{ width: '8pt', flexShrink: 0 }}>•</Text>
                            <Text style={{ flex: 1, paddingLeft: '3pt' }}>{skill.name}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  </View>
                );
              }
              case 'work_experience': {
                const workItems = (section.items as Experience[]).filter((w) => !w.hidden);
                const militarySection = resumeData.sections.find((s) => s.type === 'military_experience');
                const militaryItems = ((militarySection?.items as MilitaryExperience[] | undefined) ?? []).filter(
                  (m) => !m.hidden
                );

                if (workItems.length === 0 && militaryItems.length === 0) return null;

                type CombinedRow = { kind: 'work'; data: Experience } | { kind: 'military'; data: MilitaryExperience };

                const combined: CombinedRow[] = [
                  ...workItems.map((w): CombinedRow => ({ kind: 'work', data: w })),
                  ...militaryItems.map((m): CombinedRow => ({ kind: 'military', data: m })),
                ];

                combined.sort((a, b) => compareExperienceDates(a.data, b.data));

                return (
                  <View
                    key={section.type}
                    style={{
                      ...styles.flexCol,
                      marginBottom: spacing[2],
                    }}
                  >
                    <Text
                      style={{
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        borderBottom: '1px solid black',
                        marginBottom: spacing[1],
                      }}
                    >
                      {t('createResume.pdfDocumentTemplate.workExperience')}
                    </Text>

                    {combined.map((row, index) => {
                      const dateLine =
                        formatDate(row.data.start_date) +
                        ' – ' +
                        (row.data.end_date
                          ? formatDate(row.data.end_date)
                          : t('createResume.pdfDocumentTemplate.present'));

                      if (row.kind === 'military') {
                        const mil = row.data;
                        const { branchLabel, rankLabel } = getMilitaryLabels(mil);
                        const headline = [branchLabel, mil.title].filter(Boolean).join(' - ');
                        return (
                          <View key={`mil-${index}`} style={{ marginBottom: spacing[2] }}>
                            <View style={{ ...styles.flexRowBetween, alignItems: 'flex-start' }}>
                              <Text style={{ fontWeight: 'bold', flex: 1, paddingRight: spacing[1] }}>{headline}</Text>
                              <Text style={{ flexShrink: 0 }}>{dateLine}</Text>
                            </View>
                            {rankLabel && <Text>{rankLabel}</Text>}
                            {mil.comments && mil.comments.length > 0 && <Text>{mil.comments}</Text>}
                            {mil.skills.length > 0 && (
                              <View style={styles.workExperienceSkills}>
                                <Text
                                // style={{ marginBottom: spacing[1] }}
                                >
                                  {t('createResume.pdfDocumentTemplate.skills')}:
                                </Text>
                                <Text>
                                  {mil.skills.map((skill, i) => (
                                    <Fragment key={i}>
                                      {i === 0 ? '• ' : ' • '}
                                      {skill.name}
                                    </Fragment>
                                  ))}
                                </Text>
                              </View>
                            )}
                          </View>
                        );
                      }

                      const work = row.data;
                      return (
                        <View key={`work-${index}`} style={{ marginBottom: spacing[2] }}>
                          <View style={{ ...styles.flexRowBetween, alignItems: 'flex-start' }}>
                            <Text style={{ fontWeight: 'bold', flex: 1, paddingRight: spacing[1] }}>
                              {work.employer_provider} - {work.title}
                            </Text>
                            <Text style={{ flexShrink: 0 }}>{dateLine}</Text>
                          </View>
                          {work.comments && work.comments.length > 0 && <Text>{work.comments}</Text>}
                          {work.skills.length > 0 && (
                            <View style={styles.workExperienceSkills}>
                              <Text
                              // style={{ marginBottom: spacing[1] }}
                              >
                                {t('createResume.pdfDocumentTemplate.skills')}:
                              </Text>
                              <Text>
                                {work.skills.map((skill: { name: string }, i: number) => (
                                  <Fragment key={i}>
                                    {i === 0 ? '• ' : ' • '}
                                    {skill.name}
                                  </Fragment>
                                ))}
                              </Text>
                            </View>
                          )}
                        </View>
                      );
                    })}
                  </View>
                );
              }
              case 'military_experience':
                // Military entries are now rendered inline within the Work Experience section,
                // sorted chronologically with the user's other jobs.
                return null;
              case 'others': {
                if (section.items.length <= 0) return null;
                if ((section.items as Experience[]).every((e) => e.hidden)) return null;
                const sortedEducation = (section.items as Experience[])
                  .filter((e) => !e.hidden)
                  .slice()
                  .sort(compareExperienceDates);
                return (
                  <View
                    key={section.type}
                    style={{
                      ...styles.flexCol,
                      marginBottom: spacing[2],
                    }}
                  >
                    <Text
                      style={{
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        borderBottom: '1px solid black',
                        marginBottom: spacing[1],
                      }}
                    >
                      {t('createResume.pdfDocumentTemplate.educationAndCertifications')}
                    </Text>

                    {sortedEducation.map((education, index) => {
                      const degreeLevelOption = education.level
                        ? EDUCATION_LEVEL_BY_ID.get(Number(education.level))
                        : null;
                      const educationTypeLabelId = EDUCATION_TYPE_LABEL_BY_KEY.get(education.type);
                      // JST academic degrees store the raw credential code (e.g. "bachDegree") as
                      // the type, so resolve it through the shared credential label map first.
                      const credentialLabelId = (EDUCATION_CREDENTIAL_LABEL_IDS as unknown as Record<string, string>)[
                        education.type
                      ];
                      const educationLabel =
                        education.type === 'degree' && education.level
                          ? degreeLevelOption
                            ? t(degreeLevelOption.labelId)
                            : education.level
                          : credentialLabelId
                          ? t(credentialLabelId)
                          : educationTypeLabelId ||
                            // LCW/DCC-sourced credentials (e.g. "openbadge") carry raw external
                            // type strings that don't map to an i18n key.
                            (lcwTypeLabels as Record<string, string>)[education.type] ||
                            education.type;

                      return (
                        <View key={index} style={{ marginBottom: spacing[2] }}>
                          <View style={{ ...styles.flexRowBetween, alignItems: 'flex-start' }}>
                            <View style={{ flex: 1, paddingRight: spacing[1] }}>
                              <Text style={{ fontWeight: 'bold' }}>
                                {educationLabel}
                                {education.title ? ` - ${education.title}` : ''}
                              </Text>
                              <Text>{education.employer_provider}</Text>
                            </View>
                            <Text style={{ flexShrink: 0 }}>
                              {formatDateRangeOrSingle(
                                education.start_date,
                                education.end_date,
                                t('createResume.pdfDocumentTemplate.present')
                              )}
                            </Text>
                          </View>

                          <View style={styles.flexCol}>
                            {education.comments && <Text>{education.comments}</Text>}
                            {education.skills.length > 0 && (
                              <View style={styles.workExperienceSkills}>
                                <Text
                                // style={{ marginBottom: spacing[1] }}
                                >
                                  {t('createResume.pdfDocumentTemplate.skills')}:
                                </Text>
                                <Text>
                                  {education.skills.map((skill: { name: string }, index: number) => (
                                    <Fragment key={index}>
                                      {index === 0 ? '• ' : ' • '}
                                      {skill.name}
                                    </Fragment>
                                  ))}
                                </Text>
                              </View>
                            )}
                          </View>
                        </View>
                      );
                    })}
                  </View>
                );
              }
              case 'references':
                if (section.items.length <= 0) return null;
                return (
                  <View
                    key={section.type}
                    style={{
                      ...styles.flexCol,
                      marginBottom: spacing[2],
                    }}
                  >
                    <Text
                      style={{
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        borderBottom: '1px solid black',
                        marginBottom: spacing[1],
                      }}
                    >
                      {t('createResume.pdfDocumentTemplate.references')}
                    </Text>

                    {section.items.map((ref, index) => (
                      <View key={index} style={{ marginBottom: spacing[2] }}>
                        <Text>
                          {ref.type === 'personal'
                            ? t('createResume.pdfDocumentTemplate.personalReference')
                            : t('createResume.pdfDocumentTemplate.professionalReference')}
                        </Text>
                        <Text>{ref.name}</Text>
                        {ref.title_or_role && <Text>{ref.title_or_role}</Text>}
                        {ref.employer && <Text>{ref.employer}</Text>}
                        {ref.phone_number && <Text>{formatPhoneNumber(ref.phone_number)}</Text>}
                        {ref.email && (
                          <Link
                            style={{
                              color: 'black',
                              textDecoration: 'none',
                            }}
                            src={`mailto:${ref.email}`}
                          >
                            {ref.email}
                          </Link>
                        )}
                      </View>
                    ))}
                  </View>
                );
              default:
                return null;
            }
          })}
        </View>
      </Page>
    </Document>
  );
}

export default PDFDocumentTemplate;
